import { Fragment, memo, useRef, useState } from 'react';
import { Table as TableMaterial, TableContainer } from '@mui/material';
import TableHead from './head';
import TableBody from './table-body';
import TablePagination from './table-pagination';
import { BaseTableRows, HeadCell } from './types';
import { Order } from 'shell/view-models/order';
import { ActiveTableActions } from 'system/components/shared/list-item-cta-menu/types';
import { useElementScrollWidthObserver } from 'shell/hooks/use-element-observer/scroll-width-observer';

interface TableProps<T extends BaseTableRows, K extends keyof T> {
  count?: number;
  rowsPerPage?: number;
  page: number;
  onPageChange: (value: number) => void;
  onRowsPerPageChange: (value: number) => void;
  rowsPerPageOptions?: Array<number>;
  onSort: (orders: Order) => void;
  head: Array<HeadCell<T, K>>;
  orders: Order;
  activeActions?: ActiveTableActions;
  body: Array<T>;
  onVisibilityPress?: (cell: T, page?: number) => Promise<T> | void;
  onEditPress?: (cell: T, page?: number) => Promise<T> | void;
  onTrashPress?: (cell: T, page?: number) => Promise<T> | void;
  ariaLabel: string;
  isServer?: boolean;
  hideButtons?: boolean;
  isNotLayoutFixed?: boolean;
  noSort?: boolean;
  minWidth?: number | null;
  hasClientSort?: boolean;
}

const TableComponent = <T extends BaseTableRows, K extends keyof T>({
  count,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions,
  onSort,
  head,
  orders,
  activeActions,
  body,
  onVisibilityPress,
  onEditPress,
  onTrashPress,
  ariaLabel,
  isServer,
  hideButtons,
  isNotLayoutFixed,
  noSort,
  minWidth = 950,
  hasClientSort,
}: TableProps<T, K>) => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [, setHasRef] = useState(false);

  const hasScroll = useElementScrollWidthObserver(tableRef.current as HTMLElement);

  return (
    <Fragment>
      <TableContainer
        ref={(ref) => {
          tableRef.current = ref;
          setHasRef(true);
        }}
      >
        <TableMaterial
          sx={{ minWidth }}
          size="medium"
          stickyHeader
          aria-label={ariaLabel}
          style={isNotLayoutFixed ? undefined : { tableLayout: 'fixed' }}
        >
          <TableHead
            onSort={onSort}
            head={head}
            orders={orders}
            noSort={noSort}
            hasHorizontalScroll={hasScroll}
          />
          <TableBody
            activeActions={activeActions}
            page={page}
            rowsPerPage={rowsPerPage}
            body={body}
            head={head}
            hasClientSort={hasClientSort}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            isServer={isServer}
            orders={orders}
            hideButtons={hideButtons}
            hasHorizontalScroll={hasScroll}
          />
        </TableMaterial>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Fragment>
  );
};

TableComponent.displayName = 'Table Component';

const Table = memo(TableComponent);

export default Table as typeof TableComponent;
